import axios, { AxiosResponse } from 'axios';
import { parse } from 'json-bigint';
import { isLocal } from './util';
import { LOCAL_URLS, POST_REQUEST_HEADERS, API_TIMEOUTS } from './const';
import { TATGResponse, TAddToCarTATGResponse } from '../shared/types/apiTypes';
import { getParam } from './urlHelper';
import { ADDRESS_ID_PARAM, SERVER_ID_PARAM } from '../shared/params/params';

export interface TAddToCartRequest {
  itemProductId?: string;
  mroRepoId?: string;
  _dynSessConf: string;
  channel: string;
  bestServerId: string;
  addressId: string;
}

/**
 * Check normal http response code
 * @param response AxiosResponse
 */
export const checkStatus = (response: AxiosResponse) => {
  if (!response) {
    throw new Error('API returned insufficient data');
  }
  const { status } = response;
  if (status !== 200) {
    throw new Error(`${status}`);
  }
  return response;
};

/**
 * ATG API always returns HTTP status 200 for internal errors (product not found etc.)
 * thus actual http errors will not get caught, this function is needed to process those errors.
 * @param response AxiosResponse
 */
export const checkATGStatus = (response: AxiosResponse) => {
  const res = response?.data?.response as TATGResponse;

  if (!response) {
    throw new Error('API returned insufficient data');
  }

  const { responseCode, responseMsg } = res;

  if (responseCode !== '0') {
    throw new Error(`${responseCode}: ${responseMsg}`);
  }
  return response;
};

/**
 * Gets the session id
 * @param apiUrl url to the session api
 */
export const getSessionId = async (apiUrl: string) => {
  const sessionResponse = await axios.get(apiUrl, {
    transformResponse: data => {
      // parse using JSONBigInt
      return parse(data);
    },
  });

  return sessionResponse?.data?.sessionConfirmationNumber;
};

/**
 * Makes the add to cart post with fetching the session
 * @param productId - id of the product that we want to add to cart, this can be a SKU id or MRO id
 * @param sessionId - id of the current session
 * @param baseUrl - url to endpoint base (absolute URL to server)
 * @param addToCartUrl - add to cart endpoint (relative path)
 * @param isMro - true if the product is MRO
 */
export const addToCart = async (
  productId: string,
  sessionId: string,
  baseUrl: string,
  addToCartUrl: string,
  isMro: boolean,
  addressId?: string,
  serverId?: string
) => {
  const resolvedServerId = serverId || getParam(window.location.search, SERVER_ID_PARAM);
  const resolvedAddressId = addressId || getParam(window.location.search, ADDRESS_ID_PARAM);

  const params: TAddToCartRequest = {
    _dynSessConf: sessionId,
    channel: 'online',
    [isMro ? 'mroRepoId' : 'itemProductId']: productId,
    bestServerId: resolvedServerId,
    addressId: resolvedAddressId,
  };

  const local = isLocal();

  const response = await axios.request<TAddToCarTATGResponse>({
    url: local ? `../data/${LOCAL_URLS.ADD_TO_CART}` : `${baseUrl}${addToCartUrl}`,
    method: local ? 'GET' : 'POST',
    [local ? 'params' : 'data']: params,
    headers: POST_REQUEST_HEADERS,
    timeout: API_TIMEOUTS.ADD_TO_CART_POST,
  });

  return response.data;
};
